import { useContext, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Check, ChevronDown } from 'lucide-react';
import { MODAL_SKLO } from '../../lib/uiTokens';
import { SkloCtx, skloMaterial } from '../ui/Sklo';

/**
 * Výběr období seznamu objednávek — přepínač mezi „živě" a historií.
 *
 * Do 2026-10-06 byly Živě a Historie dvě záložky a období se volilo ve
 * filtru Historie. Teď je to jedno ovládání vedle hledání: **Dnes** obnovuje
 * seznam živě (WebSocket, „K dořešení"), cokoli jiného je historie s filtry,
 * stránkováním a exportem. Uživatel neřeší, ve které záložce dnešní
 * objednávka leží — jen posune období.
 *
 * Nabídku skládá volající (`volby`) — bez přístupu k historii dostane jen
 * Dnes a Včera.
 *
 * @param {{obdobi: string, od: ?string, do: ?string}} props.hodnota
 * @param {Array<{value: string, label: string, kratce?: string}>} props.volby
 * @param {(obdobi: string, od?: ?string, do?: ?string) => void} props.onChange
 * @param {string} [props.popisVlastni] - popisek vybraného vlastního období („1. 9. – 6. 10.")
 * @param {string} [props.poznamka] - drobný text pod nabídkou (proč chybí delší období)
 */
export default function ObdobiVolba({ hodnota, volby, onChange, popisVlastni = null, poznamka = null, kompaktni = false, isDark, mutedColor, barva }) {
    const [otevreno, setOtevreno] = useState(false);
    const [pozice, setPozice] = useState(null);
    const [vlastniOd, setVlastniOd] = useState(hodnota.od || '');
    const [vlastniDo, setVlastniDo] = useState(hodnota.do || '');
    const tlacitkoRef = useRef(null);
    const panelRef = useRef(null);
    const sklo = useContext(SkloCtx) && kompaktni;

    const zive = hodnota.obdobi === 'dnes';
    const aktivni = volby.find(v => v.value === hodnota.obdobi);
    const popis = hodnota.obdobi === 'vlastni' && popisVlastni ? popisVlastni : (aktivni?.kratce || aktivni?.label || 'Období');

    const spocitejPozici = () => {
        const r = tlacitkoRef.current?.getBoundingClientRect();
        if (!r) return;
        const sirka = 248;
        setPozice({ left: Math.min(Math.max(8, r.left), window.innerWidth - sirka - 8), top: r.bottom + 6, sirka, maxVyska: window.innerHeight - r.bottom - 20 });
    };
    useLayoutEffect(() => { if (otevreno) spocitejPozici(); }, [otevreno]);

    useEffect(() => {
        if (!otevreno) return undefined;
        const mimo = (e) => {
            if (tlacitkoRef.current?.contains(e.target) || panelRef.current?.contains(e.target)) return;
            setOtevreno(false);
        };
        const klavesa = (e) => { if (e.key === 'Escape') setOtevreno(false); };
        document.addEventListener('mousedown', mimo);
        document.addEventListener('keydown', klavesa);
        window.addEventListener('resize', spocitejPozici);
        return () => {
            document.removeEventListener('mousedown', mimo);
            document.removeEventListener('keydown', klavesa);
            window.removeEventListener('resize', spocitejPozici);
        };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [otevreno]);

    const hranice = isDark ? 'rgba(255,255,255,0.12)' : 'var(--border-default)';
    const datumPole = {
        flex: 1, minWidth: 0, height: 34, borderRadius: 8, padding: '0 8px', fontSize: 13, outline: 'none',
        background: isDark ? 'rgba(0,0,0,0.3)' : 'var(--surface-raised)', color: 'var(--text-primary)',
        border: `1px solid ${hranice}`, colorScheme: isDark ? 'dark' : 'light',
    };

    // Vlastní období začne od toho, co bylo vidět (doplní volající) — pole se srovnají
    useEffect(() => { setVlastniOd(hodnota.od || ''); setVlastniDo(hodnota.do || ''); }, [hodnota.od, hodnota.do]);

    const vyber = (value) => {
        onChange(value);
        // Vlastní období se doladí v polích pod nabídkou a potvrdí — jinak by
        // každé ťuknutí do data načítalo seznam znovu
        if (value !== 'vlastni') setOtevreno(false);
    };

    // Živá tečka: Dnes se obnovuje sám, ať je to na tlačítku poznat
    const tecka = zive ? (
        <span aria-hidden="true" className="animate-pulse" style={{ width: 7, height: 7, borderRadius: 999, background: barva, flexShrink: 0 }} />
    ) : null;

    return (
        <>
            <button ref={tlacitkoRef} type="button" onClick={() => setOtevreno(o => !o)}
                aria-haspopup="listbox" aria-expanded={otevreno} title={zive ? 'Dnes — obnovuje se živě' : 'Období'}
                className="inline-flex items-center"
                style={sklo ? {
                    height: 44, borderRadius: 999, border: 'none', padding: '0 14px', gap: 7, flexShrink: 0,
                    ...skloMaterial(isDark), color: zive ? barva : 'var(--text-primary)',
                    fontSize: 14, fontWeight: 700, cursor: 'pointer', WebkitTapHighlightColor: 'transparent',
                } : {
                    height: kompaktni ? 40 : 32, borderRadius: 8, padding: '0 10px', gap: 6, flexShrink: 0,
                    border: `1px solid ${zive ? `${barva}66` : hranice}`,
                    background: zive ? `${barva}1a` : 'transparent',
                    color: zive ? barva : 'var(--text-primary)',
                    fontSize: 12.5, fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap',
                }}>
                {tecka}
                <span style={{ fontVariantNumeric: 'tabular-nums' }}>{popis}</span>
                <ChevronDown size={sklo ? 15 : 13} style={{ opacity: 0.7, transform: otevreno ? 'rotate(180deg)' : 'none', transition: 'transform .15s' }} />
            </button>

            {otevreno && pozice && createPortal(
                <div ref={panelRef} role="listbox" aria-label="Období" style={{
                    position: 'fixed', zIndex: 9999, left: pozice.left, top: pozice.top, width: pozice.sirka,
                    maxHeight: Math.max(240, pozice.maxVyska), overflowY: 'auto',
                    ...MODAL_SKLO, padding: 6, display: 'flex', flexDirection: 'column', gap: 1,
                }}>
                    {volby.map(v => {
                        const vybrano = v.value === hodnota.obdobi;
                        return (
                            <button key={v.value} type="button" role="option" aria-selected={vybrano} onClick={() => vyber(v.value)}
                                className="flex items-center text-left"
                                style={{
                                    gap: 8, minHeight: kompaktni ? 44 : 34, padding: '0 10px', borderRadius: 7, border: 'none', cursor: 'pointer',
                                    background: vybrano ? `${barva}1f` : 'transparent', color: vybrano ? barva : 'var(--text-primary)',
                                    fontSize: 13, fontWeight: vybrano ? 600 : 500,
                                }}>
                                <span style={{ width: 14, display: 'inline-flex' }}>{vybrano && <Check size={14} strokeWidth={2.6} />}</span>
                                <span className="flex-1">{v.label}</span>
                                {v.value === 'dnes' && <span style={{ fontSize: 11, color: mutedColor }}>živě</span>}
                            </button>
                        );
                    })}
                    {hodnota.obdobi === 'vlastni' && (
                        <div style={{ padding: '8px 6px 4px', display: 'flex', flexDirection: 'column', gap: 8, borderTop: `1px solid ${hranice}`, marginTop: 4 }}>
                            <div className="flex items-center" style={{ gap: 6 }}>
                                <input type="date" aria-label="Od" value={vlastniOd} max={vlastniDo || undefined}
                                    onChange={e => setVlastniOd(e.target.value)} style={datumPole} />
                                <span style={{ color: mutedColor, fontSize: 12 }}>–</span>
                                <input type="date" aria-label="Do" value={vlastniDo} min={vlastniOd || undefined}
                                    onChange={e => setVlastniDo(e.target.value)} style={datumPole} />
                            </div>
                            <button type="button" onClick={() => { onChange('vlastni', vlastniOd || null, vlastniDo || null); setOtevreno(false); }}
                                disabled={!vlastniOd && !vlastniDo}
                                className="rounded-lg"
                                style={{ height: 34, border: 'none', background: barva, color: '#fff', fontSize: 13, fontWeight: 600, cursor: 'pointer', opacity: !vlastniOd && !vlastniDo ? 0.5 : 1 }}>
                                Použít
                            </button>
                        </div>
                    )}
                    {poznamka && <p style={{ margin: '6px 10px 4px', fontSize: 11.5, lineHeight: 1.4, color: mutedColor }}>{poznamka}</p>}
                </div>,
                document.body,
            )}
        </>
    );
}
